import React, { ChangeEvent, ReactNode, Ref, useRef } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { FormItem, ValidationState } from '../Form/FormItem';
import { SvgIconButton } from '../SvgIconButton/SvgIconButton';

export const InputElement = styled.input<Pick<InputProps, 'isMini' | 'isValueBold' | 'Icon' | 'hasCharCount' | 'type'>>`
  ${({ theme }) => theme.partials.newForm.item.default};
  ${({ isMini, theme }) => (isMini ? theme.partials.newForm.item.mini : null)};
  color: ${({ isMini, theme, color }) =>
    isMini ? theme.vars.newColor.blue2 : theme.vars.newColor.darkGS1 || color ? color : theme.vars.newColor.darkGS1};
  font-weight: ${({ isValueBold }) => (isValueBold ? '600' : '500')};
  padding-right: ${({ Icon, type }) => (Icon ? '40px !important' : type === 'number' ? '33px' : '15px')};
  &:focus-within {
    padding-right: ${({ hasCharCount, type }) =>
      hasCharCount ? '16px !important' : type === 'number' ? '33px' : null};
    margin-right: ${({ hasCharCount }) => (hasCharCount ? '0 !important' : null)};
  }
  &:disabled,
  &:disabled:hover,
  &.disabled,
  &.disabled:hover {
    cursor: not-allowed;
    background-color: ${({ isMini, theme }) => (!isMini ? theme.designToken.neutral50 : null)};
    color: ${({ isMini, theme }) => (isMini ? theme.vars.newColor.darkGS3 : theme.designToken.neutral500)};
  }

  ${props => {
    if (props.type === 'time') {
      return css`
        &::-webkit-calendar-picker-indicator {
          display: none;
        }
      `;
    }
  }}

  ${props => {
    if (props.type === 'number') {
      return css`
        /* stylelint-disable property-no-vendor-prefix */
        /* Works for Chrome, Safari, Edge, Opera */
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }

        /* Works for Firefox */
        &[type='number'] {
          -moz-appearance: textfield;
        }
      `;
    }
  }}

  &[placeholder] {
    text-overflow: ellipsis;
  }
`;

export const IconContainer = styled.div<Pick<InputProps, 'Icon' | 'focusMode'>>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 17px 0 7px;
  width: 40px;
  visibility: ${(props: any) => (props.Icon && !props.focusMode ? 'visible' : 'hidden')};
`;

const NumberInputContainer = styled.div<Pick<InputProps, 'disabled'>>`
  position: absolute;
  right: ${({ theme }) => theme.designToken.spacing3};
  bottom: 10px;
  > button {
    padding: 0;
    border: none;
  }
  > button[data-testid='SvgIconButton']:hover {
    padding: 0;
    background: transparent;
    border: none;
  }
  path {
    fill: ${({ disabled, theme }) => disabled && theme.designToken.neutral500};
  }
`;

const InputRoot = styled.div<{ fullWidth?: boolean }>`
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'auto')};
  min-width: ${({ fullWidth }) => (fullWidth ? '0' : 'auto')};
`;

export interface InputProps extends IntrinsicProps {
  /** Input ARIA label tag */
  ariaLabel?: string;
  /** Input label */
  label?: string;
  /** Will be passed through to the labels for attribute */
  inputId?: string;
  /** Input type */
  type?: string;
  /** Specifies whether the input is required  */
  required?: boolean;
  /** Specifies whether the input is disabled  */
  disabled?: boolean;
  /** Input placeholder */
  placeholder?: string;
  /** Input value */
  value?: string | number;
  /** Input name */
  name?: string;
  /** Input tabindex */
  tabIndex?: number;
  /** Automatically focus when page loads */
  autoFocus?: boolean;
  /** Invoke onChange of input  */
  onChange?: (value?: string, e?: ChangeEvent<HTMLInputElement>) => void;
  /** Invoke onBlur of input */
  onBlur?: () => void;
  /** Input validation state (complete, warning)  */
  validationState?: ValidationState;
  /** Input validation message  */
  validationMessage?: string;
  /** Adds spacing below the component (default: true) */
  spacingBelow?: boolean;
  /** A hint can be passed as HTML to explain what the input is used for */
  hint?: any;
  /** Optional tooltip heading. Defaults to undefined. */
  tooltipHeading?: string;
  /** Optional tooltip description. Defaults to undefined. */
  tooltipDescription?: string | string[];
  /** Optional tooltip width. Defaults to auto */
  tooltipWidth?: string;
  /** Optional tooltip to display. This replaces the existing tooltip component. */
  tooltipComponent?: ReactNode;
  /** Specifies the minimum value allowed */
  min?: string;
  /** Specifies the maximum value allowed */
  max?: string;
  /** className of input */
  className?: string;
  /** testId of input */
  inputTestId?: string;
  /** If enabled shows the mini version of the date picker. (Default: false) */
  isMini?: boolean;
  /** If the label text needs to be bold. (Default: false) */
  isLabelBold?: boolean;
  /** If the input text needs to be bold. (Default: false) */
  isValueBold?: boolean;
  /** If the input text needs to have Icon.*/
  Icon?: React.ReactNode;
  /** If the input text is in darkMode.*/
  darkMode?: boolean;
  /** If the input text has char count.*/
  hasCharCount?: boolean;
  /** If the input text on focus.*/
  focusMode?: boolean;
  /** Invoke onFocus of input */
  onFocus?: () => void;
  /** charachter Input length */
  charLength?: number;
  /** Input minWidth  */
  minWidth?: string;
  /** Input maxWidth  */
  maxWidth?: string;
  /** Allows the input to span the full width of its container */
  fullWidth?: boolean;
  /** maxLength charachter Input */
  maxLength?: number;
  searchIcon?: boolean;
  isBottomCharCount?: boolean;
  numberInputUnit?: string;
  pattern?: string;
  color?: string;
}

export const Input = React.forwardRef(
  (
    {
      ariaLabel,
      autoFocus,
      className,
      color,
      disabled = false,
      hint,
      inputId,
      inputTestId,
      isMini,
      isLabelBold,
      isValueBold,
      label,
      min,
      name,
      onChange,
      onBlur,
      onFocus,
      placeholder,
      required,
      spacingBelow,
      tabIndex,
      tooltipDescription,
      tooltipHeading,
      tooltipWidth,
      tooltipComponent,
      type,
      validationMessage,
      validationState,
      value,
      Icon,
      hasCharCount,
      focusMode,
      charLength,
      maxLength,
      max,
      minWidth,
      maxWidth,
      fullWidth,
      searchIcon,
      isBottomCharCount,
      numberInputUnit,
      ...rest
    }: InputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement>();
    const theme = useTheme();

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e.target.value, e);
      }
    };

    const handleBlur = () => {
      if (onBlur) {
        onBlur();
      }
    };

    const handleFocus = () => {
      if (onFocus) {
        onFocus();
      }
    };

    const onUpButtonClick = e => {
      e.preventDefault();
      if (disabled) return;

      if (inputRef.current) {
        const initialValue = Number(inputRef.current.value);
        const maxValue = Number(max);
        const finalValue = String(initialValue + 1);
        if (maxValue !== undefined && initialValue >= maxValue) {
          return;
        }
        inputRef.current.value = finalValue;
        if (onChange) onChange(finalValue);
      }
    };

    const onDownButtonClick = e => {
      e.preventDefault();
      if (disabled) return;

      if (inputRef.current) {
        const initialValue = Number(inputRef.current.value);
        const minValue = Number(min);
        const finalValue = String(initialValue - 1);
        if (minValue !== undefined && initialValue <= minValue) {
          return;
        }
        inputRef.current.value = finalValue;
        if (onChange) onChange(finalValue);
      }
    };

    return (
      <InputRoot className={className} fullWidth={fullWidth}>
        <FormItem
          label={label}
          color={color}
          validationMessage={validationMessage}
          validationState={validationState}
          spacingBelow={spacingBelow}
          hint={hint}
          tooltipHeading={tooltipHeading}
          tooltipDescription={tooltipDescription}
          tooltipWidth={tooltipWidth}
          tooltipComponent={tooltipComponent}
          disabled={disabled}
          for={inputId}
          isMini={isMini}
          isLabelBold={isLabelBold}
          hasCharCount={hasCharCount}
          charLength={charLength}
          focusMode={focusMode}
          minWidth={minWidth}
          maxWidth={maxWidth}
          fullWidth={fullWidth}
          isBottomCharCount={isBottomCharCount}
          type={type}
          numberInputUnit={numberInputUnit}
        >
          <InputElement
            type={type}
            required={required}
            disabled={disabled}
            placeholder={placeholder}
            onChange={handleChange}
            onBlur={handleBlur}
            value={value}
            name={name}
            tabIndex={tabIndex}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            aria-label={ariaLabel}
            autoComplete={type === 'email' ? 'email' : 'off'}
            id={inputId}
            min={min}
            max={max}
            maxLength={maxLength}
            data-testid={inputTestId}
            isMini={isMini}
            color={color}
            isValueBold={isValueBold}
            Icon={Boolean(Icon)}
            onFocus={handleFocus}
            hasCharCount={hasCharCount}
            ref={ref || inputRef}
            {...rest}
          />
          {Icon && !focusMode ? (
            <IconContainer Icon={Boolean(Icon)} focusMode={focusMode}>
              {Icon}
            </IconContainer>
          ) : (
            ''
          )}
          {type === 'number' && (
            <NumberInputContainer disabled={disabled}>
              <SvgIconButton
                ariaLabel="caret-up"
                name="caret-up"
                color={
                  value && Number(max) && Number(value) >= Number(max)
                    ? theme.designToken.neutral300
                    : theme.designToken.neutral700
                }
                onClick={onUpButtonClick}
              />
              <SvgIconButton
                ariaLabel="caret-down"
                name="caret-down"
                color={
                  value && Number(min) && Number(value) <= Number(min)
                    ? theme.designToken.neutral300
                    : theme.designToken.neutral700
                }
                onClick={onDownButtonClick}
              />
            </NumberInputContainer>
          )}
        </FormItem>
      </InputRoot>
    );
  },
);

Input.displayName = 'Input';

export default Input;
